import './style.css';

// ----------------------------------------------------
// PRODUCT CATALOGUE DATA (Soni Exports components)
// ----------------------------------------------------

interface Product {
  id: string;
  title: string;
  sku: string;
  category: string;
  image: string;
  desc: string;
  detailedDesc: string;
  specs: Record<string, string>;
  cadSvg: string;
}

const PRODUCTS: Product[] = [
  {
    id: 'linen',
    title: 'Custom Linen & Cotton Essentials',
    sku: 'SE-LN-01',
    category: 'linen',
    image: '/garment_linen.png',
    desc: 'Bespoke lightweight shirts, dresses, and tunics crafted from high-grade organic linen and combed cotton, tailored to Japanese minimalist styles.',
    detailedDesc: 'Our linen and cotton lines are created specifically for lightweight comfort and premium texture. Sourced from organic flax mills, we combine these fabrics with double-needle tailoring and clean french seams to ensure durability. We work directly with your customized fit patterns and grading rules to produce items perfect for boutique retail lines.',
    specs: {
      'Fabric Composition': '100% Organic Linen / Combed Cotton',
      'Stitch Density': '18-20 SPI (Stitches Per Inch)',
      'Shrinkage Ratio': 'JIS L1042 Standard &lt; 1%',
      'Seam Type': 'Flat-Felled & French Seams',
      'Button Options': 'Natural Shell / Coconut Husk buttons',
      'Weight Options': '140 gsm (Lightweight) to 220 gsm (Medium)'
    },
    cadSvg: `
      <svg viewBox="0 0 200 200" width="100%" height="100%" class="cad-schematic">
        <rect x="10" y="10" width="180" height="180" fill="none" stroke="#dfcfbe" stroke-width="0.75" stroke-dasharray="2,2" />
        <!-- Shirt Schematic -->
        <path d="M 60,40 L 80,30 L 100,35 L 120,30 L 140,40 L 160,70 L 140,80 L 135,65 L 135,160 L 65,160 L 65,65 L 60,80 L 40,70 Z" fill="none" stroke="#dfcfbe" stroke-width="1.5" />
        <!-- Collar Details -->
        <path d="M 80,30 L 100,50 L 120,30" fill="none" stroke="#dfcfbe" stroke-width="1" />
        <path d="M 80,30 L 100,42 L 120,30" fill="none" stroke="#dfcfbe" stroke-width="0.75" />
        <!-- Placket & Buttons -->
        <line x1="100" y1="50" x2="100" y2="160" stroke="#dfcfbe" stroke-width="1.2" />
        <circle cx="100" cy="70" r="2" fill="#dfcfbe" />
        <circle cx="100" cy="95" r="2" fill="#dfcfbe" />
        <circle cx="100" cy="120" r="2" fill="#dfcfbe" />
        <circle cx="100" cy="145" r="2" fill="#dfcfbe" />
        <!-- Pocket Detail -->
        <rect x="110" y="70" width="18" height="22" rx="1" fill="none" stroke="#dfcfbe" stroke-width="0.8" />
        <!-- Stitch lines -->
        <path d="M 65,155 L 135,155" stroke="#dfcfbe" stroke-width="0.5" stroke-dasharray="1.5,1.5" />
        <path d="M 45,72 L 58,78" stroke="#dfcfbe" stroke-width="0.5" stroke-dasharray="1.5,1.5" />
        <path d="M 155,72 L 142,78" stroke="#dfcfbe" stroke-width="0.5" stroke-dasharray="1.5,1.5" />
        <text x="15" y="185" fill="#dfcfbe" font-family="Space Grotesk" font-size="8">Linen Shirt Spec v1.2</text>
        <text x="110" y="25" fill="#dfcfbe" font-family="Space Grotesk" font-size="7">±0.5mm Tolerance</text>
      </svg>
    `
  },
  {
    id: 'outerwear',
    title: 'Structured Utility Outerwear',
    sku: 'SE-OW-04',
    category: 'outerwear',
    image: '/garment_outerwear.png',
    desc: 'Heavyweight utility jackets, blousons, and structured trenches manufactured using reinforced canvas and cotton-nylon weather resistant threads.',
    detailedDesc: 'Built for longevity and weather performance, our utility outerwear items use double-needle chain stitching, bar-tacked stress points, and custom copper/brass rivets. We construct using organic heavy duck canvas or custom nylon blends, and configure options such as flannel lining, hidden pockets, and premium YKK zippers.',
    specs: {
      'Shell Material': '12oz Heavyweight Organic Duck Canvas',
      'Lining Options': '100% Cotton Flannel / Quilted Satin',
      'Reinforcements': 'Bar-tacks on all stress joints',
      'Thread Type': 'Heavy Duty Cotton-wrapped Poly Core',
      'Zippers': 'YKK Japan Metal Zippers (No: 5 / No: 8)',
      'Finishing': 'Enzyme garment wash for vintage softness'
    },
    cadSvg: `
      <svg viewBox="0 0 200 200" width="100%" height="100%" class="cad-schematic">
        <rect x="10" y="10" width="180" height="180" fill="none" stroke="#dfcfbe" stroke-width="0.75" stroke-dasharray="2,2" />
        <!-- Jacket outline -->
        <path d="M 55,40 L 75,32 L 100,38 L 125,32 L 145,40 L 170,80 L 150,90 L 140,75 L 140,165 L 60,165 L 60,75 L 50,90 L 30,80 Z" fill="none" stroke="#dfcfbe" stroke-width="1.5" />
        <!-- Zipper Centerline -->
        <line x1="100" y1="38" x2="100" y2="165" stroke="#dfcfbe" stroke-width="2" stroke-dasharray="3,1" />
        <!-- Collar -->
        <path d="M 75,32 L 100,55 L 125,32" fill="none" stroke="#dfcfbe" stroke-width="1.2" />
        <path d="M 68,31 C 75,42 90,52 100,55 C 110,52 125,42 132,31" fill="none" stroke="#dfcfbe" stroke-width="0.8" />
        <!-- Large utility pockets -->
        <rect x="68" y="115" width="26" height="30" rx="2" fill="none" stroke="#dfcfbe" stroke-width="1" />
        <rect x="106" y="115" width="26" height="30" rx="2" fill="none" stroke="#dfcfbe" stroke-width="1" />
        <!-- Pocket Flaps -->
        <path d="M 68,115 L 81,120 L 94,115 Z" fill="none" stroke="#dfcfbe" stroke-width="0.8" />
        <path d="M 106,115 L 119,120 L 132,115 Z" fill="none" stroke="#dfcfbe" stroke-width="0.8" />
        <!-- Shoulder seams and flatlock stitching -->
        <path d="M 75,32 L 60,75" stroke="#dfcfbe" stroke-width="0.75" stroke-dasharray="2,2" />
        <path d="M 125,32 L 140,75" stroke="#dfcfbe" stroke-width="0.75" stroke-dasharray="2,2" />
        <text x="15" y="185" fill="#dfcfbe" font-family="Space Grotesk" font-size="8">Utility Jacket Spec v2.1</text>
        <text x="132" y="95" fill="#dfcfbe" font-family="Space Grotesk" font-size="8">YKK Metal</text>
      </svg>
    `
  },
  {
    id: 'knitwear',
    title: 'High-Gauge Premium Knitwear',
    sku: 'SE-KW-08',
    category: 'knitwear',
    image: '/garment_knitwear.png',
    desc: 'On-demand fine-gauge knitted cardigans, sweaters, and fleece mid-layers with anti-pilling treatment and smart rib knit details.',
    detailedDesc: 'Our knitwear collection features premium long-staple cotton and wool yarns. Crafted on high-precision circular and flat knitting machines (12GG/14GG), it offers superb dimensional recovery, breathability, and comfort. Pre-washed to eliminate shrinkage, it meets strict Japanese JIS quality guidelines.',
    specs: {
      'Knit Gauge': '12GG Fine Knit and 14GG High-Gauge',
      'Yarn Source': 'Long-staple Supima Cotton / Australian Merino Wool',
      'Stitching Tech': 'Fully-fashioned panel integration',
      'Anti-Pilling': 'Singeing & bio-polishing enzyme finish',
      'Ribbing': '2x2 Lycra-reinforced rib cuff & hem',
      'Color Fastness': 'JIS L0844 Compliant (Grade 4.5+)'
    },
    cadSvg: `
      <svg viewBox="0 0 200 200" width="100%" height="100%" class="cad-schematic">
        <rect x="10" y="10" width="180" height="180" fill="none" stroke="#dfcfbe" stroke-width="0.75" stroke-dasharray="2,2" />
        <!-- Knit Sweater outline -->
        <path d="M 62,42 L 78,35 L 122,35 L 138,42 L 175,70 L 155,85 L 138,72 L 138,155 L 62,155 L 62,72 L 45,85 L 25,70 Z" fill="none" stroke="#dfcfbe" stroke-width="1.5" />
        <!-- Ribbed Neckline -->
        <path d="M 78,35 C 80,45 120,45 122,35" fill="none" stroke="#dfcfbe" stroke-width="1.2" />
        <path d="M 78,35 C 80,47 120,47 122,35" fill="none" stroke="#dfcfbe" stroke-width="0.8" stroke-dasharray="1.5,1.5" />
        <!-- Ribbed Cuff Details -->
        <line x1="171" y1="67" x2="152" y2="82" stroke="#dfcfbe" stroke-width="1.2" />
        <line x1="174" y1="70" x2="155" y2="85" stroke="#dfcfbe" stroke-width="1.2" />
        <line x1="29" y1="67" x2="48" y2="82" stroke="#dfcfbe" stroke-width="1.2" />
        <line x1="26" y1="70" x2="45" y2="85" stroke="#dfcfbe" stroke-width="1.2" />
        <!-- Ribbed Hem Detail -->
        <rect x="62" y="155" width="76" height="10" fill="none" stroke="#dfcfbe" stroke-width="1.2" />
        <path d="M 66,155 L 66,165 M 71,155 L 71,165 M 76,155 L 76,165 M 81,155 L 81,165 M 86,155 L 86,165 M 91,155 L 91,165 M 96,155 L 96,165 M 101,155 L 101,165 M 106,155 L 106,165 M 111,155 L 111,165 M 116,155 L 116,165 M 121,155 L 121,165 M 126,155 L 126,165 M 131,155 L 131,165 M 134,155 L 134,165" stroke="#dfcfbe" stroke-width="0.75" />
        <text x="15" y="185" fill="#dfcfbe" font-family="Space Grotesk" font-size="8">High-Gauge Knitwear Spec</text>
        <text x="142" y="110" fill="#dfcfbe" font-family="Space Grotesk" font-size="7">14GG Supima</text>
      </svg>
    `
  }
];

// ----------------------------------------------------
// UI REFERENCES
// ----------------------------------------------------

const catalogGrid = document.getElementById('catalog-grid');
const searchInput = document.getElementById('catalogue-search') as HTMLInputElement;
const filterButtons = document.querySelectorAll('.filter-btn');
const noResultsMessage = document.getElementById('no-results-message');

const header = document.querySelector('.header');
const mobileToggle = document.getElementById('mobile-toggle');
const navLinks = document.getElementById('nav-links');

const productModal = document.getElementById('product-modal');
const modalCloseBtn = document.getElementById('modal-close-btn');
const modalProductVisual = document.getElementById('modal-product-visual');
const modalProductSku = document.getElementById('modal-product-sku');
const modalProductTag = document.getElementById('modal-product-tag');
const modalProductTitle = document.getElementById('modal-product-title');
const modalProductDesc = document.getElementById('modal-product-desc');
const modalSpecsTable = document.getElementById('modal-specs-table');
const modalCtaBtn = document.getElementById('modal-cta-btn');

const inquiryForm = document.getElementById('inquiry-form') as HTMLFormElement;
const formProductSelect = document.getElementById('form-product') as HTMLSelectElement;

let currentFilter = 'all';
let searchQuery = '';

// ----------------------------------------------------
// CATALOGUE ENGINE (Search & Filter Logic)
// ----------------------------------------------------

function initCatalogue() {
  renderCatalog();

  // Search input event listener
  if (searchInput) {
    searchInput.addEventListener('input', (e) => {
      searchQuery = (e.target as HTMLInputElement).value.toLowerCase().trim();
      renderCatalog();
    });
  }

  // Filter buttons event listeners
  filterButtons.forEach(btn => {
    btn.addEventListener('click', () => {
      // Remove active state from current buttons
      filterButtons.forEach(b => b.classList.remove('active'));
      // Add active state to clicked
      btn.classList.add('active');

      currentFilter = btn.getAttribute('data-category') || 'all';
      renderCatalog();
    });
  });
}

function renderCatalog() {
  if (!catalogGrid) return;

  // Clear existing items in grid
  catalogGrid.innerHTML = '';

  // Filter items
  const filteredProducts = PRODUCTS.filter(prod => {
    const matchesCategory = currentFilter === 'all' || prod.category === currentFilter;
    const matchesSearch = prod.title.toLowerCase().includes(searchQuery) ||
                          prod.sku.toLowerCase().includes(searchQuery) ||
                          prod.desc.toLowerCase().includes(searchQuery) ||
                          Object.entries(prod.specs).some(([k, v]) => k.toLowerCase().includes(searchQuery) || v.toLowerCase().includes(searchQuery));
    return matchesCategory && matchesSearch;
  });

  // Display no results if empty
  if (filteredProducts.length === 0) {
    if (noResultsMessage) noResultsMessage.style.display = 'flex';
  } else {
    if (noResultsMessage) noResultsMessage.style.display = 'none';

    filteredProducts.forEach(prod => {
      const card = document.createElement('article');
      card.className = 'product-card';
      card.setAttribute('data-category', prod.category);
      card.setAttribute('id', `card-${prod.id}`);

      // Generate spec list elements
      const specsHtml = Object.entries(prod.specs).slice(0, 3).map(([key, val]) => `
        <li><strong>${key}:</strong> <span>${val}</span></li>
      `).join('');

      card.innerHTML = `
        <div class="card-img-wrapper">
          <img src="${prod.image}" alt="${prod.title}" loading="lazy" class="card-img" />
        </div>
        <div class="card-body">
          <div class="card-header-meta">
            <span class="card-sku">SKU: ${prod.sku}</span>
            <span class="card-badge">${prod.category}</span>
          </div>
          <h3 class="card-title">${prod.title}</h3>
          <p class="card-desc">${prod.desc}</p>
          <ul class="card-specs">
            ${specsHtml}
          </ul>
          <button class="btn outline-btn card-cta" data-product-id="${prod.id}">View Technical Specs</button>
        </div>
      `;

      catalogGrid.appendChild(card);

      // Event listener for CTA button
      const ctaBtn = card.querySelector('.card-cta');
      if (ctaBtn) {
        ctaBtn.addEventListener('click', () => {
          openDetailsModal(prod.id);
        });
      }
    });
  }
}

// ----------------------------------------------------
// DETAILS MODAL CONTROLLER
// ----------------------------------------------------

function openDetailsModal(productId: string) {
  const prod = PRODUCTS.find(p => p.id === productId);
  if (!prod || !productModal) return;

  // Fill content
  if (modalProductSku) modalProductSku.innerText = `SKU: ${prod.sku}`;
  if (modalProductTag) modalProductTag.innerText = prod.category;
  if (modalProductTitle) modalProductTitle.innerText = prod.title;
  if (modalProductDesc) modalProductDesc.innerText = prod.detailedDesc;

  // Insert schematic SVG
  if (modalProductVisual) {
    modalProductVisual.innerHTML = prod.cadSvg;
  }

  // Insert Specifications Table
  if (modalSpecsTable) {
    modalSpecsTable.innerHTML = Object.entries(prod.specs).map(([key, val]) => `
      <tr>
        <td>${key}</td>
        <td>${val}</td>
      </tr>
    `).join('');
  }

  // Configure quick inquiry CTA inside modal
  if (modalCtaBtn) {
    // Clean old listeners by replacement
    const newBtn = modalCtaBtn.cloneNode(true) as HTMLButtonElement;
    modalCtaBtn.parentNode?.replaceChild(newBtn, modalCtaBtn);

    newBtn.addEventListener('click', () => {
      // Close modal
      closeDetailsModal();
      // Set value in select option
      if (formProductSelect) {
        formProductSelect.value = prod.id;
      }
      // Smooth scroll to inquiry card
      const targetElement = document.getElementById('contact');
      if (targetElement) {
        targetElement.scrollIntoView({ behavior: 'smooth' });
      }
      // Focus name input in inquiry
      const nameInput = document.getElementById('form-name');
      if (nameInput) {
        setTimeout(() => nameInput.focus(), 600);
      }
    });
  }

  // Show modal
  productModal.classList.add('open');
  document.body.style.overflow = 'hidden'; // Lock scrolling
}

function closeDetailsModal() {
  if (!productModal) return;
  productModal.classList.remove('open');
  document.body.style.overflow = ''; // Unlock scrolling
}

if (modalCloseBtn) {
  modalCloseBtn.addEventListener('click', closeDetailsModal);
}

if (productModal) {
  // Close on outside click
  productModal.addEventListener('click', (e) => {
    if (e.target === productModal) {
      closeDetailsModal();
    }
  });

  // Close on Escape key
  window.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && productModal.classList.contains('open')) {
      closeDetailsModal();
    }
  });
}

// ----------------------------------------------------
// HEADERS & LAYOUT SCROLL ACTIONS
// ----------------------------------------------------

window.addEventListener('scroll', () => {
  if (header) {
    if (window.scrollY > 50) {
      header.classList.add('scrolled');
    } else {
      header.classList.remove('scrolled');
    }
  }
});

// Mobile menu toggle
if (mobileToggle && navLinks) {
  mobileToggle.addEventListener('click', () => {
    mobileToggle.classList.toggle('active');
    navLinks.classList.toggle('open');
  });

  // Close drawer when clicking navigation items
  const navItems = navLinks.querySelectorAll('.nav-item');
  navItems.forEach(item => {
    item.addEventListener('click', () => {
      mobileToggle.classList.remove('active');
      navLinks.classList.remove('open');
    });
  });
}

// ----------------------------------------------------
// FORM & RFQ INQUIRY HANDLER
// ----------------------------------------------------

if (inquiryForm) {
  inquiryForm.addEventListener('submit', (e) => {
    e.preventDefault();

    const submitBtn = inquiryForm.querySelector('.submit-btn') as HTMLButtonElement;
    const initialText = submitBtn.innerHTML;

    // Show loading transition
    submitBtn.disabled = true;
    submitBtn.innerHTML = '<span>Compiling Quote Request...</span>';

    setTimeout(() => {
      // Form fields values
      const name = (document.getElementById('form-name') as HTMLInputElement).value;
      const rfqId = `RFQ-${Math.floor(1000 + Math.random() * 9000)}`;

      showToast(`Success! Quote request ${rfqId} submitted for ${name}.`);

      // Reset Form
      inquiryForm.reset();
      submitBtn.disabled = false;
      submitBtn.innerHTML = initialText;
    }, 1500);
  });
}

function showToast(message: string) {
  const existingToast = document.querySelector('.toast-notification');
  if (existingToast) {
    existingToast.remove();
  }

  const toast = document.createElement('div');
  toast.className = 'toast-notification';
  toast.innerHTML = `<span>${message}</span>`;

  document.body.appendChild(toast);

  // Animation delay
  requestAnimationFrame(() => {
    toast.style.transform = 'translateX(-50%) translateY(0)';
  });

  setTimeout(() => {
    toast.style.transform = 'translateX(-50%) translateY(100px)';
    toast.style.opacity = '0';
    setTimeout(() => toast.remove(), 500);
  }, 4000);
}

// ----------------------------------------------------
// INITIALIZATION
// ----------------------------------------------------

document.addEventListener('DOMContentLoaded', () => {
  initCatalogue();
});

// Run directly in case DOMContentLoaded has already fired (common with Vite dev server)
if (document.readyState === 'interactive' || document.readyState === 'complete') {
  initCatalogue();
}
